<template>
  <div>
    <loading :active.sync="isLoading" :can-cancel="false" color="#007BFF" :is-full-page="true" />

    <div class="container-fluid mt-6">
      <div class="card">
        <div class="card-header border-0">
          <h3 class="mb-0 title-2 bold">Listado de Niveles — On Demand</h3>
          <LevelPaymentBetaNotice class="mt-3 mb-0" />
        </div>
        <div class="row starter-page">
          <div class="container-fluid">
            <div v-if="loadFailed" class="levels-failed">{{ LOAD_ERROR }}</div>
            <vue-content-loading
              v-else-if="getPeriods === null"
              :width="300"
              :height="120"
              primary="#bdc3c7"
              secondary="#95a5a6"
            >
              <svg:rect x="0" y="0" rx="3" ry="3" width="280" height="8" />
              <svg:rect x="0" y="20" rx="3" ry="3" width="280" height="8" />
              <svg:rect x="0" y="40" rx="3" ry="3" width="280" height="8" />
              <svg:rect x="0" y="60" rx="3" ry="3" width="280" height="8" />
            </vue-content-loading>
            <el-table
              v-else
              :data="getPeriods"
              row-key="id"
              size="small"
              header-row-class-name="thead-desing"
              class="desing"
            >
              <el-table-column label="Período" min-width="140px">
                <template v-slot="{ row }">{{ periodLabel(row) }}</template>
              </el-table-column>

              <el-table-column label="Quincena" min-width="120px">
                <template v-slot="{ row }">{{ fortnightLabel(row) }}</template>
              </el-table-column>

              <el-table-column label="Puntaje" min-width="84px">
                <template v-slot="{ row }">
                  <strong v-if="row.score !== null">{{ row.score }}</strong>
                  <span v-else class="levels-empty">sin datos</span>
                </template>
              </el-table-column>

              <el-table-column label="Nivel" min-width="84px">
                <template v-slot="{ row }">
                  <LevelBadge v-if="row.level" :level="row.level" />
                  <span v-else class="levels-empty">sin datos</span>
                </template>
              </el-table-column>

              <el-table-column label="Estado" min-width="150px">
                <template v-slot="{ row }">
                  <LevelStatusBadge :period="row" :thresholds="getThresholds" />
                </template>
              </el-table-column>

              <el-table-column
                v-for="threshold in getThresholds"
                :key="threshold.metric"
                :label="threshold.label"
                :render-header="metricHeader"
                min-width="130px"
              >
                <template v-slot="{ row }">
                  <LevelMetricCell
                    :metric="threshold.metric"
                    :period="row"
                    :threshold="threshold"
                    :clickable="threshold.metric === incidentsMetric"
                    @detail="openClaims(row)"
                  />
                </template>
              </el-table-column>
            </el-table>

            <div
              class="mt-4 col-12 d-flex align-items-center justify-content-center justify-content-sm-between flex-wrap"
            >
              <p v-if="getPeriods !== null" class="card-category">
                Mostrando {{ getPeriods.length }} de {{ getPagination.total }} quincenas
              </p>
              <base-pagination
                v-if="getPagination.total > getPagination.perPage"
                v-model="page"
                class="pagination-no-border"
                :per-page="getPagination.perPage"
                :total="getPagination.total"
                @input="load"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <ClaimsModal
      :show="claimsOpen"
      :claims="getClaims"
      :mismatch="claimsMismatch"
      :orders-count="claimsOrders"
      @close="closeClaims"
    />
  </div>
</template>

<script>
import { Table, TableColumn } from 'element-ui';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import moment from 'moment';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { VueContentLoading } from 'vue-content-loading';
import LevelMetricCell from './components/LevelMetricCell';
import LevelStatusBadge from './components/LevelStatusBadge';
import LevelBadge from '@/components/Levels/LevelBadge';
import ClaimsModal from './components/ClaimsModal';
import LevelPaymentBetaNotice from '@/components/Levels/LevelPaymentBetaNotice';

const METRIC_INCIDENTS = 'incidents';
const FIRST_PAGE = 1;
const DAY_MONTH_FORMAT = 'DD/MM';
const NO_ORDERS = 0;
const METRIC_HEAD_FONT_SIZE = '10px';
const METRIC_HEAD_FONT_WEIGHT = '400';
const METRIC_HEAD_COLOR = '#a3a3a3';
const METRIC_HEAD_LINE_HEIGHT = '1.3';
const LOAD_ERROR =
  'No se pudieron cargar tus niveles. Recargá la página para volver a intentarlo.';

export default {
  name: 'FoodcourtLevelsList',
  components: {
    Loading,
    VueContentLoading,
    LevelMetricCell,
    LevelStatusBadge,
    LevelBadge,
    ClaimsModal,
    LevelPaymentBetaNotice,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  data() {
    return {
      LOAD_ERROR,
      isLoading: false,
      loadFailed: false,
      page: FIRST_PAGE,
      claimsOpen: false,
      claimsOrders: NO_ORDERS
    };
  },
  computed: {
    ...mapGetters('foodcourtLevels', [
      'getPeriods',
      'getPagination',
      'getThresholds',
      'getClaims',
      'claimsMismatch'
    ]),
    incidentsMetric() {
      return METRIC_INCIDENTS;
    }
  },
  async created() {
    await this.load(FIRST_PAGE);
  },
  methods: {
    ...mapActions('foodcourtLevels', ['GET_LEVELS', 'GET_CLAIMS']),
    ...mapMutations('foodcourtLevels', ['CLEAR_CLAIMS']),
    async load(page) {
      try {
        this.isLoading = true;
        this.loadFailed = false;
        await this.GET_LEVELS(page);
      } catch (error) {
        this.loadFailed = true;
        this.$notify({ type: 'danger', message: LOAD_ERROR });
      } finally {
        this.isLoading = false;
      }
    },
    weightLabel(threshold) {
      const floor =
        threshold.floor_weight === null ? '' : ` (piso ${this.toInt(threshold.floor_weight)}%)`;

      return `pond. ${this.toInt(threshold.weight)}%${floor}`;
    },
    metricHeader(h, { column }) {
      const threshold = this.getThresholds.find(item => item.label === column.label);

      return h('div', [
        h('div', { class: 'metric-head__label' }, column.label),
        h(
          'div',
          {
            style: {
              fontSize: METRIC_HEAD_FONT_SIZE,
              fontWeight: METRIC_HEAD_FONT_WEIGHT,
              color: METRIC_HEAD_COLOR,
              lineHeight: METRIC_HEAD_LINE_HEIGHT
            }
          },
          threshold === undefined ? '' : this.weightLabel(threshold)
        )
      ]);
    },
    periodLabel(period) {
      return `Del ${this.dayMonth(period.start_date)} al ${this.dayMonth(period.end_date)}`;
    },
    fortnightLabel(period) {
      return `Semanas ${this.week(period.start_date)} y ${this.week(period.end_date)}`;
    },
    dayMonth(date) {
      return moment(date).format(DAY_MONTH_FORMAT);
    },
    week(date) {
      return moment(date).isoWeek();
    },
    toInt(value) {
      return Math.round(Number(value));
    },
    openClaims(period) {
      this.isLoading = true;
      this.claimsOrders = Number(period.orders_count);
      this.GET_CLAIMS({ start: period.start_date, end: period.end_date })
        .then(() => {
          this.claimsOpen = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    closeClaims() {
      this.claimsOpen = false;
      this.CLEAR_CLAIMS();
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .el-table .cell {
  word-break: break-word !important;
}
::v-deep .el-table .cell strong {
  font-size: 0.95rem;
}
::v-deep .el-table .cell .level-desing {
  padding: 8px 14px;
}
::v-deep .metric-head__label {
  font-weight: 600;
  line-height: 1.3;
}
::v-deep .el-table th .cell,
::v-deep .el-table th div {
  white-space: normal !important;
  text-overflow: unset !important;
  overflow: visible !important;
  line-height: 1.3;
}
.levels-empty {
  font-size: 0.75rem;
  font-style: italic;
  color: #8a988f;
}
.levels-failed {
  font-size: 0.9rem;
  color: #a2681a;
  background: rgba(251, 176, 59, 0.12);
  border: 1px solid rgba(251, 176, 59, 0.5);
  border-radius: 10px;
  padding: 12px 14px;
}
</style>
